import type { SessionMetricPoint } from "api/types/charts";
import type { DistributionDatum } from "../Targets/TargetDistributionChart";
import { compareNumericStringKeys } from "./compareNumericStringKeys";
import { derivePromptDistribution } from "./derivePromptDistribution";

/**
 * Derives the bar rows for a distribution chart segment from a target's
 * session metrics. Returns null when the segment isn't a distribution segment
 * or its backing field has no data. Shared by TargetChartArea and the doc-gen
 * GoalProgressCard so the two surfaces cannot drift.
 */
export function buildDistributionData(
  segmentId: string | null | undefined,
  sessionMetrics: SessionMetricPoint[]
): DistributionDatum[] | null {
  if (!segmentId) return null;

  // Returns the most-recent session that has a non-empty value for the field.
  // Appropriate for per-session snapshot metrics (rating distribution, latency
  // by prompt level) where the latest reading is what matters clinically.
  const latestMap = <T extends Record<string, number>>(
    selector: (point: SessionMetricPoint) => T | undefined
  ): T | null => {
    for (let i = sessionMetrics.length - 1; i >= 0; i--) {
      const value = selector(sessionMetrics[i]);
      if (value && Object.keys(value).length > 0) return value;
    }
    return null;
  };

  // Aggregates a numeric map field across ALL sessions by summing each key.
  // Appropriate for cumulative tallies (prompt level counts, step errors)
  // where a single session's snapshot would omit data from other sessions.
  const aggregateMap = <T extends Record<string, number>>(
    selector: (point: SessionMetricPoint) => T | undefined
  ): T | null => {
    const acc: Record<string, number> = {};
    let hasData = false;
    for (const point of sessionMetrics) {
      const value = selector(point);
      if (!value) continue;
      for (const [k, v] of Object.entries(value)) {
        if (!Number.isFinite(v)) continue;
        acc[k] = (acc[k] ?? 0) + v;
        hasData = true;
      }
    }
    return hasData ? (acc as T) : null;
  };

  if (segmentId === "prompt-level-distribution") {
    const bars = derivePromptDistribution(sessionMetrics, {
      field: "prompt_level_distribution",
      aggregate: "sum",
    });
    return bars.length > 0 ? bars : null;
  }

  if (segmentId === "step-level-errors") {
    const map = aggregateMap((point) => point.step_level_errors);
    if (!map) return null;
    return Object.entries(map)
      .sort((a, b) => compareNumericStringKeys(a[0], b[0]))
      .map(([step, value]) => ({ label: `Step ${step}`, value }));
  }

  if (segmentId === "rating-distribution") {
    const map = latestMap((point) => point.rating_distribution);
    if (!map) return null;
    return Object.entries(map)
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([rating, value]) => ({ label: rating, value }));
  }

  if (segmentId === "latency-by-prompt-level") {
    const bars = derivePromptDistribution(sessionMetrics, {
      field: "latency_by_prompt_level",
      aggregate: "mean",
      transform: (ms) => ms / 1000,
    });
    return bars.length > 0 ? bars : null;
  }

  return null;
}
